import { onBeforeUnmount, onMounted, ref, Ref } from "vue";

/**
 * The zoom that makes content of width `needed` fit into `available`, rounded down to whole percents
 * so the result never overflows by a fraction. Below `minZoom` the content counts as too wide instead.
 */
export function fitZoom(available: number, needed: number, minZoom: number) {
    if (needed <= 0 || available >= needed) {
        return { zoom: 1, tooWide: false };
    }
    const zoom = Math.floor((available / needed) * 100) / 100;
    return zoom < minZoom ? { zoom: 1, tooWide: true } : { zoom, tooWide: false };
}

// Once too wide, only come back when this much room is left over, so the two layouts never flip back
// and forth at the threshold.
const RETURN_MARGIN = 16;

/**
 * Shrinks an element that is too wide for its container as a whole with CSS `zoom`, which scales fonts,
 * paddings, buttons and icons at once and, unlike a transform, also the space the element takes.
 * Below `minZoom` it gives up and reports `tooWide`, so the caller can show a narrower layout.
 *
 * @param probe   an element spanning the available width in every layout (it is never zoomed itself)
 * @param target  the element to zoom, or undefined while the narrower layout is shown
 * @param measure the width the target needs at zoom 1
 * @param hold    true while the layout must not change under the user (e.g. while editing): the target
 *                then stays at `minZoom` instead of being reported too wide
 */
export function useFitZoom(
    probe: Ref<HTMLElement | null>,
    target: () => HTMLElement | undefined,
    measure: (target: HTMLElement) => number,
    minZoom: number,
    hold: () => boolean = () => false
) {
    const zoom = ref(1);
    const tooWide = ref(false);
    let neededWhenTooWide = 0;

    function fit() {
        const available = probe.value?.clientWidth ?? 0;
        const element = target();
        if (!element) {
            if (tooWide.value && available >= neededWhenTooWide * minZoom + RETURN_MARGIN) {
                // The target renders again and gets measured on the next fit.
                tooWide.value = false;
            }
            return;
        }

        // Measure unzoomed, then apply the new zoom in the same task: nothing is painted in between.
        element.style.zoom = "1";
        const needed = measure(element);
        let result = fitZoom(available, needed, minZoom);
        if (result.tooWide && hold()) {
            result = { zoom: minZoom, tooWide: false };
        }
        if (result.tooWide) {
            neededWhenTooWide = needed;
        }
        zoom.value = result.zoom;
        tooWide.value = result.tooWide;
        element.style.zoom = result.zoom === 1 ? "" : String(result.zoom);
    }

    let frame = 0;
    function scheduleFit() {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(fit);
    }

    let observer: ResizeObserver | undefined;
    onMounted(() => {
        if (probe.value && typeof ResizeObserver !== "undefined") {
            observer = new ResizeObserver(scheduleFit);
            observer.observe(probe.value);
        }
        scheduleFit();
    });
    onBeforeUnmount(() => {
        observer?.disconnect();
        cancelAnimationFrame(frame);
    });

    return { zoom, tooWide, fit, scheduleFit };
}
